import React                     from 'react';
import { DrawTriggerOptions }    from '@tokamap/core';
import { MapFraming }            from '@tokamap/core';
import { framingFromLegacyView } from '@tokamap/core';
import { resolveFramingGeometry } from '@tokamap/core';
import { shapesExtentPoints }    from '@tokamap/core';
import useForceUpdate            from '@widesk-core/hooks/useForceUpdate';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';

const LIVE: DrawTriggerOptions = { skipHistory: true, skipSave: true, skipConfirm: true };

function legacyFraming(): MapFraming {
	const editor = currentEditorStore.get;
	const origin = currentEditorStore.documentOrigin;
	const view = editor.getMapView();

	return framingFromLegacyView({
		center: origin?.center ?? view.center,
		zoom: origin?.zoom ?? view.zoom,
		zoomMax: origin?.zoomMax,
		rotationDeg: origin?.rotationDeg ?? 0,
	});
}

export default function useFraming() {
	const editor = currentEditorStore.get;
	const forceUpdate = useForceUpdate();
	const shapesRevision = currentEditorStore.shapesRevision;

	React.useEffect(() => {
		return editor.mapAdapter.on(event => {
			if (
				event.type === 'framing:changed'
				|| event.type === 'allow-pitch:changed'
				|| event.type === 'tile-layer:changed'
			) forceUpdate();
		});
	}, [editor]);

	const stored = editor.getFraming();
	const current = stored ?? legacyFraming();
	const framingKey = JSON.stringify(current);
	const framing = React.useMemo(() => current, [framingKey]);

	const contentPoints = React.useMemo(
		() => shapesExtentPoints(editor.getAllShapes()),
		[editor, shapesRevision],
	);

	const geometry = React.useMemo(
		() => resolveFramingGeometry(framing, contentPoints),
		[framing, contentPoints],
	);

	const update = (patch: Partial<MapFraming>, live = false) => {
		editor.setFraming({ ...framing, ...patch }, live ? LIVE : undefined);
	};

	return {
		framing,
		framingKey,
		shapesRevision,
		isLegacy: !stored,
		geometry,
		contentPoints,
		isPlaced: editor.getTileLayerName() !== 'unset',
		allowPitch: editor.getAllowPitch(),
		update,
	};
}
